import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AuthenticationCard } from '@components/atoms/AuthenticationCard';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Space } from '@components/atoms/Space';
import { Typography, TypographyAppearanceVariants } from '@components/atoms/Typography';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useVerifyAccount } from '@services/useVerifyAccount';
import { Loader } from '@components/molecules/Pagination';

export const VerifyAccountCard: FC = () => {
    const { navigate } = useCustomNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const { request, loading, success, errorMessage, lastResponseTime } = useVerifyAccount();

    const translations = {
        title: t('verify-account-card-component.title'),
        subtitle: t('verify-account-card-component.subtitle'),
        successTitle: t('verify-account-card-component.success.title'),
        successMessage: t('verify-account-card-component.success.message'),
        errorTitle: t('verify-account-card-component.error.title'),
        errorMessage: t('verify-account-card-component.error.message'),
        errorInvalidParams: t('verify-account-card-component.error.invalid-params'),
        loginButton: t('verify-account-card-component.login-button'),
        tryAgainButton: t('verify-account-card-component.try-again-button'),
    };

    useEffect(() => {
        if (token && email) {
            request({ token, email });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, email]);

    const handleGoToLogin = () => {
        navigate(`/${SITE_URLS.login}`);
    };

    const renderContent = () => {
        if (!token || !email) {
            return (
                <>
                    <Typography
                        align="center"
                        appearanceLevel={TypographyAppearanceVariants.level6}
                        color="danger">
                        {translations.errorTitle}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Typography align="center" fontSize="16" color="muted">
                        {translations.errorInvalidParams}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    <Button
                        label={translations.loginButton}
                        classes="d-block w-100"
                        mode={ButtonMode.primary}
                        onClick={handleGoToLogin}
                    />
                </>
            );
        }

        if (loading) {
            return (
                <div className="d-flex justify-content-center">
                    <Loader />
                </div>
            );
        }

        if (lastResponseTime && success) {
            return (
                <>
                    <Typography
                        align="center"
                        appearanceLevel={TypographyAppearanceVariants.level6}
                        color="success">
                        {translations.successTitle}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Typography align="center" fontSize="16" color="muted">
                        {translations.successMessage}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    <Button
                        label={translations.loginButton}
                        classes="d-block w-100"
                        mode={ButtonMode.primary}
                        onClick={handleGoToLogin}
                    />
                </>
            );
        }

        if (lastResponseTime && errorMessage) {
            return (
                <>
                    <Typography
                        align="center"
                        appearanceLevel={TypographyAppearanceVariants.level6}
                        color="danger">
                        {translations.errorTitle}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Typography align="center" fontSize="16" color="muted">
                        {errorMessage || translations.errorMessage}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    <Button
                        label={translations.loginButton}
                        classes="d-block w-100"
                        mode={ButtonMode.primary}
                        onClick={handleGoToLogin}
                    />
                </>
            );
        }

        return (
            <div className="d-flex justify-content-center">
                <Loader />
            </div>
        );
    };

    return (
        <AuthenticationCard title={translations.title} subtitle={translations.subtitle}>
            {renderContent()}
        </AuthenticationCard>
    );
};
